// ============================================================================
// sync-recovery-detection.js — does a reference file genuinely need
// local-folder recovery?
// ----------------------------------------------------------------------------
// 2026-05-31 (Peter): the FolderReconnectBanner ("Files missing from project
// folder") was firing as a FALSE POSITIVE on every reloaded project that has
// backend-backed PDFs. Root cause: App.jsx's `needsSyncPdfRecovery` keyed off
// `pdfDataUrl` ONLY — but `data-service-api.js` strips `pdfDataUrl` on save
// whenever a PDF has durable backend backing (`pdfS3Key` / `fileMetadataId`),
// because the preview is re-fetched from the backend on demand. So after a
// reload, a perfectly previewable backend PDF carries NO `pdfDataUrl` and was
// flagged "missing"; the banner then reported the project's TOTAL pdf count.
//
// The correct rule: a file needs LOCAL-FOLDER recovery only when it has
// NEITHER usable in-memory bytes NOR any durable backend backing. A file that
// is backend-backed is previewable, not missing — and if the backend object
// is genuinely gone, the 410 FILE_ORPHAN path (FileOrphanBanner) handles it,
// which is the authoritative "truly gone" signal. The reconnect banner must
// not duplicate that.
//
// These are pure predicates so the trigger gate (`.some`) and the displayed
// counts (`.filter`) in App.jsx use ONE definition and can never drift.
// ============================================================================

import { isDocumentDerivedVisual } from './project-photo-contract';
import {
    getPlayableCloudVideoSource,
    getPlayableWorkerVideoProxy,
} from '../services/video-storage-policy';

interface DocumentLike {
    name?: unknown;
    originalName?: unknown;
    ext?: unknown;
    contentType?: unknown;
    mimeType?: unknown;
    pdfDataUrl?: unknown;
    pdfBlob?: unknown;
    pdfS3Key?: unknown;
    fileMetadataId?: unknown;
    s3Key?: unknown;
}

interface PhotoLike {
    id?: unknown;
    name?: unknown;
    source?: unknown;
    sourcePdfDocId?: unknown;
    sourcePdfName?: unknown;
    sourcePdfPage?: unknown;
    sourcePdfImageId?: unknown;
    _dataLost?: unknown;
    blobId?: unknown;
    s3Key?: unknown;
    fileMetadataId?: unknown;
    data?: unknown;
    dataUrl?: unknown;
}

interface VideoLike {
    syncFileName?: unknown;
    url?: unknown;
    file?: unknown;
    videoBlobId?: unknown;
    s3Key?: unknown;
    fileMetadataId?: unknown;
    storagePolicy?: {
        version?: unknown;
        uploadOriginalToCloud?: unknown;
    } | null;
    cloudPlayback?: {
        kind?: unknown;
        s3Key?: unknown;
        fileMetadataId?: unknown;
    } | null;
    workerProxy?: {
        mimetype?: unknown;
        base64?: unknown;
    } | null;
    _localBlobUnavailable?: unknown;
    _blobOwnershipMismatch?: unknown;
}

/** Match the local URL contract enforced by VideoPreviewPlayer. */
export function isPlayableLocalVideoUrl(url: unknown): boolean {
    return typeof url === 'string'
        && (url.startsWith('blob:') || url.startsWith('data:video/'));
}

/** Match the in-memory file contract enforced by VideoPreviewPlayer. */
export function isPlayableLocalVideoFile(file: unknown): boolean {
    return typeof Blob !== 'undefined' && file instanceof Blob;
}

/** A document is a PDF by extension, filename, or declared MIME type. */
function isPdfDocument(doc: DocumentLike | null | undefined): boolean {
    const name = String(doc?.name || doc?.originalName || '').toLowerCase();
    const ext = String(doc?.ext || name.split('.').pop() || '').toLowerCase();
    const mime = String(doc?.contentType || doc?.mimeType || '').toLowerCase();
    return ext === 'pdf' || name.endsWith('.pdf') || mime === 'application/pdf';
}

/**
 * True only when a PDF document has no way to render — no inline bytes
 * (`pdfDataUrl` blob:/data:, or a `pdfBlob`) AND no durable backend backing
 * (`pdfS3Key`, `fileMetadataId`, or `s3Key`). Non-PDF documents are never
 * flagged here (PDF recovery is the only document recovery path).
 *
 * @param doc normalized document (post normalizeDocumentsForProjectLoad)
 */
export function pdfNeedsLocalRecovery(doc: DocumentLike | null | undefined): boolean {
    if (!doc || !isPdfDocument(doc)) return false;
    const src = String(doc.pdfDataUrl || '');
    const hasInlineBytes = src.startsWith('blob:') || src.startsWith('data:application/pdf');
    const hasBlob = !!doc.pdfBlob;
    const hasBackendBacking = !!(doc.pdfS3Key || doc.fileMetadataId || doc.s3Key);
    return !hasInlineBytes && !hasBlob && !hasBackendBacking;
}

/**
 * True when a photo has lost its data and has no durable backing. Mirrors the
 * durable-backing definition used by data-service-api.js
 * (`blobId || s3Key || fileMetadataId`) plus the in-memory paths
 * (`data` / `dataUrl`). `_dataLost` forces recovery for a genuine customer
 * photo, but never promotes a strictly proven document-derived audit visual
 * into the project-photo recovery lane.
 */
export function photoNeedsLocalRecovery(photo: PhotoLike | null | undefined): boolean {
    if (!photo) return false;
    // PDF XObjects/page renders are auxiliary views of a retained source
    // document, not independently supplied customer photos. The strict shared
    // classifier requires every provenance field, so a marker-only lookalike
    // still fails closed as a real photo that needs recovery.
    if (isDocumentDerivedVisual(photo)) return false;
    if (photo._dataLost) return true;
    const hasAnySource = !!(
        photo.blobId || photo.s3Key || photo.fileMetadataId || photo.data || photo.dataUrl
    );
    return !hasAnySource;
}

/**
 * True when a video was synced from a local folder (`syncFileName`) but has no
 * playable session source or a source VideoPreviewPlayer can resolve. Local
 * blob refs that the project-load ownership pass has proved missing/foreign
 * never count as backing. A video with no `syncFileName` is not a folder-sync
 * recovery case.
 */
export function videoNeedsLocalRecovery(video: VideoLike | null | undefined): boolean {
    if (!video || !video.syncFileName) return false;
    if (isPlayableLocalVideoUrl(video.url) || isPlayableLocalVideoFile(video.file)) return false;
    const localBlobId = Number(video.videoBlobId);
    const hasVerifiedLocalBlob = Number.isSafeInteger(localBlobId)
        && localBlobId > 0
        && !video._localBlobUnavailable
        && !video._blobOwnershipMismatch;
    const hasPlayableWorkerProxy = !!getPlayableWorkerVideoProxy(video);
    const hasPlayableCloudSource = !!getPlayableCloudVideoSource(video);
    return !hasVerifiedLocalBlob && !hasPlayableWorkerProxy && !hasPlayableCloudSource;
}
